import React, { useState, useRef, useEffect } from 'react';
import styles from './CaseModal.module.scss';
import Modal from '@/components/Modal/Modal';
import { TelegramSticker } from '@/components/TelegramSticker';
import WebApp from '@twa-dev/sdk';
import CodeLock from './CodeLock';
import {
  checkSubscription,
  openCase,
  sendProviderInfo,
  getLeaderboard,
  getAttemptsInfo,
} from '@/api/modules/Case/case';
import confetti from 'canvas-confetti';
import { useTickSound } from '@/hooks/UseTickSound';
import useAttemptsStore from '@/store/AttemptsStore';
import { useNotificationStore } from '@/store/NotificationStore';
import { TimeDisplay } from '@/utils/TimeDisplay';
import { TasksService } from '@/Services/TasksService';
import useLimitStore from '@/store/LimitStore';

interface RaffleModalProps {
  open: boolean;
  onClose: () => void;
  isSubscribed: boolean;
  setIsSubscribed: (isSubscribed: boolean) => void;
  caseData: any;
  onLeaderboardUpdate?: () => void;
}
interface AttemptsInfo {
  code_attempt: string;
  correct_count: number;
  created_at: string;
  date: string;
  id: number;
  is_opened: boolean;
  save_event_id: string;
}

interface AttemptsResponse {
  attempts_today: AttemptsInfo[];
}

const CaseModal: React.FC<RaffleModalProps> = ({
  open,
  onClose,
  isSubscribed,
  setIsSubscribed,
  caseData,
  onLeaderboardUpdate,
}) => {
  if (!open) return null;
  const tg = window?.Telegram?.WebApp;

  const [clicked, setClicked] = useState(false);
  const [code, setCode] = useState('000');
  const [matches, setMatches] = useState<number | null>(null);
  const [guessed, setGuessed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [blockedTime, setBlockedTime] = useState('');
  const [isShowingAd, setIsShowingAd] = useState(false);
  const [isProcessingCase, setIsProcessingCase] = useState(false);
  const [isWheelDisabled, setIsWheelDisabled] = useState(false);
  const [adWatched, setAdWatched] = useState(false); // Новое состояние для отслеживания просмотренной рекламы
  const attempts = useAttemptsStore((s) => s.attempts);
  const setAttempts = useAttemptsStore((s) => s.setAttempts);
  const blocked = useAttemptsStore((s) => s.blocked);
  const setBlocked = useAttemptsStore((s) => s.setBlocked);
  const setNotification = useNotificationStore((s) => s.setNotification);
  const limit = useLimitStore((state) => state.limit);
  const setLimit = useLimitStore((state) => state.setLimit);
  const shouldDisableClose = isShowingAd || isProcessingCase;
  // Мгновенная блокировка от двойных нажатий до обновления state
  const openCaseLockRef = useRef(false);
  const showCPMAdLockRef = useRef(false);
  const [attemptsInfo, setAttemptsInfo] = useState<AttemptsInfo[]>([]);

  const fetchAttemptsInfo = async () => {
    try {
      const response: AttemptsResponse = await getAttemptsInfo(
        caseData.safe_event_id,
      );
      console.log(response, 'ATTEMPTS INFO');
      setAttemptsInfo(response.attempts_today || []);
    } catch (error) {
      console.error('Error fetching attempts info:', error);
      setAttemptsInfo([]);
    }
  };

  useEffect(() => {
    fetchAttemptsInfo();
  }, [caseData.safe_event_id]);

  const getAttemptsText = (attemptsCount: number) => {
    const lastDigit = attemptsCount % 10;
    const lastTwoDigits = attemptsCount % 100;
    if (lastTwoDigits >= 11 && lastTwoDigits <= 19)
      return `${attemptsCount} попыток`;
    if (lastDigit === 1) return `${attemptsCount} попытку`;
    if (lastDigit >= 2 && lastDigit <= 4) return `${attemptsCount} попытки`;
    return `${attemptsCount} попыток`;
  };

  const handleSubscribe = () => {
    tg?.openTelegramLink?.(caseData.user.subscribed_utils.link);
    setClicked(true);
  };

  const handleCheckSubscription = async () => {
    const res = await checkSubscription(caseData.safe_event_id);
    if (res.subscribed) {
      setIsSubscribed(true);
    } else {
      setClicked(false);
      setNotification({ type: 'Error', message: 'Сперва подпишись на канал' });
    }
  };

  // 🔊 Звук победы
  const { ready, init, playOnce } = useTickSound({
    src: '/opening.wav',
    volume: 1,
  });

  // CPM реклама
  const showCPMAd = async (): Promise<boolean> => {
    // Защита от двойных нажатий
    if (showCPMAdLockRef.current) return false;
    showCPMAdLockRef.current = true;

    // Локальный замок для этого конкретного вызова функции
    let adShown = false;
    try {
      setIsShowingAd(true);
      setIsWheelDisabled(true);
      setNotification({ type: 'Warning', message: 'Загружаем рекламу...' });

      const availableTasks =
        limit >= 20
          ? await TasksService.getAllAvailableTasksNoCpcAndMonetagAndOnclick()
          : await TasksService.getAllAvailableTasksNoCpcAndMonetag();

      if (availableTasks.length > 0) {
        for (const task of availableTasks) {
          // Если уже показали рекламу в этом вызове — прекращаем
          if (adShown) {
            break;
          }
          const result: 'success' | 'user_cancelled' | 'ads_not_found' =
            await task.onClick();
          if (result === 'success') {
            try {
              await sendProviderInfo(task.provider);
              setNotification({
                type: 'Success',
                message:
                  'Реклама просмотрена! Нажмите «Открыть сейф» для попытки.',
              });
              setIsShowingAd(false);
              setIsWheelDisabled(false);
              setAdWatched(true); // Помечаем что реклама просмотрена
              // Успех: помечаем что реклама показана в этом вызове
              adShown = true;
              if (task.provider === 'onclick') {
                console.log(limit, 'LIT before');
                const newLimit = limit + 1;
                setLimit(newLimit);
                console.log(newLimit, 'LIT!!!!!');
              }
              showCPMAdLockRef.current = false;
              return true;
            } catch (e) {
              setNotification({
                type: 'Error',
                message: 'Ошибка при просмотре рекламы',
              });
              setIsShowingAd(false);
              setIsWheelDisabled(false);
              // Ошибка обработки успеха: не блокируем следующих
              showCPMAdLockRef.current = false;
              return false;
            }
          } else if (result === 'user_cancelled') {
            setNotification({
              type: 'Warning',
              message: 'Просмотр рекламы отменен',
            });
            setIsShowingAd(false);
            setIsWheelDisabled(false);
            // Отмена: помечаем что реклама показана в этом вызове
            adShown = true;
            showCPMAdLockRef.current = false;
            return false;
          } else if (result === 'ads_not_found') {
            // если провайдеров нет — пропускаем
            if (availableTasks.length === 1) {
              setNotification({
                type: 'Success',
                message: 'Нет рекламы. Нажмите «Открыть сейф» для попытки.',
              });
              setIsShowingAd(false);
              setIsWheelDisabled(false);
              setAdWatched(true); // Засчитываем как просмотренную рекламу
              showCPMAdLockRef.current = false;
              return true;
            }
            // ads_not_found: продолжаем искать без блокировки
            continue;
          }
        }
      } else {
        setNotification({
          type: 'Success',
          message: 'Нет рекламы. Нажмите «Открыть сейф» для попытки.',
        });
        setIsShowingAd(false);
        setIsWheelDisabled(false);
        setAdWatched(true); // Засчитываем как просмотренную рекламу
        showCPMAdLockRef.current = false;
        return true; // позволяем открыть сейф
      }
    } catch {
      setNotification({ type: 'Error', message: 'Ошибка при показе рекламы' });
      setIsShowingAd(false);
      setIsWheelDisabled(false);
      return false;
    } finally {
      showCPMAdLockRef.current = false;
    }
    setIsShowingAd(false);
    setIsWheelDisabled(false);
    return false;
  };

  const handleOpenCase = async () => {
    if (isLoading) return;
    if (openCaseLockRef.current) return;

    openCaseLockRef.current = true;

    try {
      // Если нужна реклама и она еще не просмотрена - показываем рекламу
      if (attempts >= 5 && !adWatched) {
        setIsLoading(true);

        // Создаем таймаут на 10 секунд
        const timeoutPromise = new Promise<boolean>((resolve) => {
          setTimeout(() => {
            setNotification({
              type: 'Success',
              message: 'Нажмите «Открыть сейф» для попытки.',
            });
            setIsShowingAd(false); // Сбрасываем состояние показа рекламы
            setIsWheelDisabled(false); // Разблокируем интерфейс
            setAdWatched(true); // Засчитываем как просмотренную при таймауте
            resolve(true);
          }, 10000); // 10 секунд
        });

        try {
          // Ждем либо ответ от рекламы, либо таймаут
          const ok = await Promise.race([showCPMAd(), timeoutPromise]);
          setIsLoading(false);
          if (!ok) {
            return;
          }
          // После показа рекламы или таймаута просто выходим
          return;
        } catch (error) {
          setIsLoading(false);
          setNotification({
            type: 'Success',
            message: 'Ошибка рекламы. Нажмите «Открыть сейф» для попытки.',
          });
          setAdWatched(true); // Засчитываем как просмотренную при ошибке
          return;
        }
      }

      // Если реклама не нужна или уже просмотрена - открываем сейф
      setIsLoading(true);
      setIsProcessingCase(true);
      setIsWheelDisabled(true);

      let vibrationInterval: NodeJS.Timeout | undefined;
      if ((window?.Telegram?.WebApp as any)?.HapticFeedback) {
        vibrationInterval = setInterval(() => {
          (
            window?.Telegram?.WebApp as any
          ).HapticFeedback?.notificationOccurred('success');
        }, 200);
      }

      await new Promise((r) => setTimeout(r, 2000));
      if (vibrationInterval) clearInterval(vibrationInterval);

      const sentCode = code.split('').map(Number);
      const res = await openCase(caseData.safe_event_id, sentCode);

      if (res.is_correct === false) {
        setAttempts(caseData.max_attempts_per_day - res.attempts_left);
        setMatches(res.match_count);
        setAdWatched(false); // Сбрасываем флаг после каждой неуспешной попытки
        // Обновляем информацию о попытках после неуспешной попытки
        await fetchAttemptsInfo();
        if (res.attempts_left === 0) {
          setBlocked(true);
          setBlockedTime(caseData.user.next_open_at);
        }
      } else if (res.is_correct === true) {
        if (!ready) await init();
        setGuessed(true);
        setBlocked(true);
        setBlockedTime(caseData.user.next_open_at);
        setAdWatched(false); // Сбрасываем флаг после успешного открытия

        try {
          await getLeaderboard(caseData.safe_event_id);
          onLeaderboardUpdate?.();
          // Обновляем информацию о попытках после успешного открытия
          await fetchAttemptsInfo();
        } catch {}

        const defaults = {
          spread: 360,
          ticks: 50,
          gravity: 0,
          decay: 0.94,
          startVelocity: 30,
          colors: ['#FFE400', '#FFBD00', '#E89400', '#FFCA6C', '#FDFFB8'],
          zIndex: 99999,
        };
        const shoot = () => {
          confetti({
            ...defaults,
            particleCount: 80,
            scalar: 1.2,
            shapes: ['star'],
          });
          confetti({
            ...defaults,
            particleCount: 30,
            scalar: 0.75,
            shapes: ['circle'],
          });
        };
        playOnce();
        setTimeout(shoot, 200);
        setTimeout(shoot, 400);
        setTimeout(shoot, 600);
        setTimeout(shoot, 800);
      } else {
        setBlocked(true);
        setBlockedTime(caseData.user.next_open_at);
        setAdWatched(false); // Сбрасываем флаг в случае блокировки
      }
    } catch (e) {
      setNotification({ type: 'Error', message: 'Ошибка при открытии сейфа' });
    } finally {
      setIsLoading(false);
      setIsProcessingCase(false);
      setIsWheelDisabled(false);
      openCaseLockRef.current = false;
    }
  };

  if (!isSubscribed) {
    return (
      <Modal open={open} onClose={onClose} disableClose={shouldDisableClose}>
        <div>
          <h2 className={styles.title}>Доступ для участников канала!</h2>
          <p className={styles.descriptionNoSubscribe}>
            Чтобы участвовать в турнире, <br />
            необходимо подписаться на наш <br />
            <span onClick={handleSubscribe} className={styles.channel}>
              официальный канал.
            </span>
          </p>

          {!clicked ? (
            <button className={styles.withdrawButton} onClick={handleSubscribe}>
              Подписаться
            </button>
          ) : (
            <button
              className={styles.withdrawButton}
              onClick={handleCheckSubscription}
            >
              Проверить подписку
            </button>
          )}
        </div>
      </Modal>
    );
  }

  if (guessed) {
    return (
      <Modal open={open} onClose={onClose} disableClose={shouldDisableClose}>
        <div>
          <h2 className={styles.title}>
            <span>Получилось! Сейф взломан.</span>
          </h2>
          <div className={styles.stickerWrapper}>
            <TelegramSticker
              tgsUrl="./Chest.tgs"
              style={{ width: 180, height: 180 }}
              loop={false}
              autoplay
            />
          </div>
          <p className={styles.description}>
            Тебе удалось подобрать правильный код за{' '}
            {getAttemptsText(attempts + 1)}. <br />
            Твой результат записан в таблицу лидеров.
          </p>
          <button className={styles.withdrawButton} onClick={onClose}>
            Продолжить
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={onClose} disableClose={shouldDisableClose}>
      <div className={styles.caseModal}>
        <h2 className={styles.title}>Подбери правильный код!</h2>

        <div className={styles.codeLock}>
          <CodeLock
            value={code}
            onChange={setCode}
            height={120}
            adPhase={isShowingAd || isProcessingCase}
            disabled={isWheelDisabled}
          />
        </div>

        {matches !== null && (
          <p className={styles.matches}>
            Совпадений: {matches}/{code.length}
          </p>
        )}

        {blocked ? (
          <button
            className={`${styles.withdrawButton} ${styles.blocked}`}
            disabled
          >
            <div className={styles.blockedContent}>
              <div className={styles.blockedTimer}>
                Повтор через <TimeDisplay endDate={blockedTime} />
              </div>
            </div>
          </button>
        ) : (
          <button
            className={`${styles.withdrawButton} ${isLoading ? styles.loading : ''}`}
            onClick={handleOpenCase}
            disabled={isLoading}
          >
            {isLoading
              ? attempts >= 5 && !adWatched
                ? 'Загружаем рекламу...'
                : 'Открываю...'
              : attempts >= 5 && !adWatched
                ? `Посмотреть рекламу, чтобы открыть`
                : attempts >= 5 && adWatched
                  ? `Открыть сейф ${attempts}/${caseData.max_attempts_per_day}`
                  : `Открыть ${attempts}/${caseData.max_attempts_per_day}`}
          </button>
        )}

        {attemptsInfo.length > 0 && (
          <div className={styles.attemptsHistory}>
            <h3 className={styles.attemptsTitle}>Последняя попытка</h3>
            <div className={styles.attemptsList}>
              {attemptsInfo.slice(0, 1).map((attempt, index) => (
                <div key={attempt.id || index} className={styles.attemptItem}>
                  <div className={styles.attemptHeader}>
                    <span className={styles.attemptCode}>
                      {attempt.code_attempt}
                    </span>
                    <span className={styles.attemptDate}>
                      {new Date(attempt.date).toLocaleDateString('ru-RU')}
                    </span>
                  </div>
                  <div className={styles.attemptDetails}>
                    <div className={styles.attemptStat}>
                      <span className={styles.statLabel}>Совпадений:</span>
                      <span className={styles.statValue}>
                        {attempt.correct_count}/3
                      </span>
                    </div>
                    <div className={styles.attemptStatus}>
                      {attempt.is_opened ? (
                        <span className={styles.statusSuccess}>✓ Открыт</span>
                      ) : (
                        <span className={styles.statusFailed}>✗ Не открыт</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CaseModal;
